import { useCallback, useRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { useUIStore } from "../../stores/ui";

interface SplitPaneProps {
  left: ReactNode;
  right: ReactNode;
  className?: string;
}

const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;

export function SplitPane({ left, right, className }: SplitPaneProps) {
  const ratio = useUIStore((s) => s.editorPreviewRatio);
  const setRatio = useUIStore((s) => s.setEditorPreviewRatio);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragging.current = true;
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      target.style.cursor = "col-resize";
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onPointerMove = (ev: PointerEvent) => {
        if (!dragging.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ev.clientX - rect.left;
        const newRatio = Math.max(MIN_RATIO, Math.min(MAX_RATIO, x / rect.width));
        setRatio(newRatio);
      };

      const onPointerUp = () => {
        dragging.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        target.style.cursor = "";
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [setRatio],
  );

  const leftPercent = `${ratio * 100}%`;
  const rightPercent = `${(1 - ratio) * 100}%`;

  return (
    <div
      ref={containerRef}
      className={cn("relative flex h-full w-full overflow-hidden", className)}
    >
      <div className="h-full min-w-0 overflow-hidden" style={{ width: leftPercent }}>
        {left}
      </div>

      <div
        onPointerDown={onPointerDown}
        className="group relative z-10 flex w-[7px] shrink-0 cursor-col-resize items-center justify-center touch-none select-none"
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={Math.round(MIN_RATIO * 100)}
        aria-valuemax={Math.round(MAX_RATIO * 100)}
      >
        {/* Base line */}
        <div className="absolute inset-y-0 w-px bg-[var(--border-hairline)] transition-all duration-200 group-hover:w-[3px] group-hover:bg-[var(--color-lever-orange)] group-hover:opacity-50 group-active:opacity-70" />

        {/* Grip dots -- visible on hover */}
        <div className="absolute flex flex-col gap-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[3px] w-[3px] rounded-full bg-[var(--text-muted)] group-hover:bg-[var(--color-lever-orange)] transition-colors duration-200"
            />
          ))}
        </div>
      </div>

      <div className="h-full min-w-0 overflow-hidden" style={{ width: rightPercent }}>
        {right}
      </div>
    </div>
  );
}
